/**
 * FO-058 (batch-18) Deel A + FO-311 (batch-34) — gedeelde types/Zod-schemas voor
 * onderhoudsschema's, de aan het schema gekoppelde planning-controle en het
 * generieke ontvangers-/kanalenmodel. Eén bron voor de API-validatie én de
 * backoffice-forms.
 *
 * FO-311: de frequentie kent vier vormen (WEKELIJKS / OM_DE_N_WEKEN / MAANDELIJKS
 * / WEKENSET). Er is geen zichtbaar "ankerweek"-veld meer; de gebruiker kiest bij
 * niet-wekelijkse cadans een "eerstvolgende keer"-datum, waaruit de API intern het
 * anker (en bij MAANDELIJKS het n-de-voorkomen) afleidt met de pure helpers in
 * `@streep/utils`. De frequentielogica staat NIET in de frontend.
 *
 * Update-schemas zijn bewust EXPLICIET all-optioneel (geen `.partial()` op een
 * schema met `.default()`): in Zod 4 behoudt `.partial()` de defaults, waardoor
 * een PATCH die een veld weglaat het stilletjes naar de default reset.
 */
import { z } from 'zod'

export const ONDERHOUD_CADANS = ['WEKELIJKS', 'TWEEWEKELIJKS', 'OM_DE_N_WEKEN', 'MAANDELIJKS', 'WEKENSET'] as const
export type OnderhoudCadans = (typeof ONDERHOUD_CADANS)[number]

export const ONDERHOUD_CADANS_LABELS: Record<OnderhoudCadans, string> = {
  WEKELIJKS: 'Wekelijks',
  TWEEWEKELIJKS: 'Om de week',
  OM_DE_N_WEKEN: 'Om de N weken',
  MAANDELIJKS: 'Maandelijks',
  WEKENSET: 'Specifieke weken',
}

export const WEEK_PARITEIT = ['EVEN', 'ONEVEN'] as const
export type WeekPariteit = (typeof WEEK_PARITEIT)[number]

export const WEEK_PARITEIT_LABELS: Record<WeekPariteit, string> = {
  EVEN: 'Even weken',
  ONEVEN: 'Oneven weken',
}

export const ONDERHOUD_CONTROLE_STATUS = ['NIET_GEPLAND', 'ONVOLLEDIG', 'ONJUIST', 'OK'] as const
export type OnderhoudControleStatusType = (typeof ONDERHOUD_CONTROLE_STATUS)[number]

export const ONDERHOUD_CONTROLE_STATUS_LABELS: Record<OnderhoudControleStatusType, string> = {
  NIET_GEPLAND: 'Niet gepland',
  ONVOLLEDIG: 'Onvolledig gepland',
  ONJUIST: 'Onjuist gepland',
  OK: 'Compleet',
}

const WEEKDAG_NAMEN = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag']

const datumIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (jjjj-mm-dd)')

/** ISO-weekdag (ma=1 .. zo=7) van een jjjj-mm-dd-string (UTC, geen tijdzone-IO). */
function isoWeekdagVanDatumString(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  return ((d.getUTCDay() + 6) % 7) + 1
}

/** Velden die de vier frequentievormen samen beschrijven (regel én mail delen dit). */
export interface FrequentieVelden {
  cadans: OnderhoudCadans
  weekdag?: number
  eerstvolgendeKeer?: string | null
  intervalWeken?: number | null
  weekPariteit?: WeekPariteit | null
  weekSet?: number[]
}

/**
 * FO-311 — validatie van de frequentievelden, gedeeld door regel- en
 * mail-automatisering-schema's. Bij niet-wekelijkse cadans is "eerstvolgende
 * keer" verplicht en moet die op de gekozen weekdag vallen; OM_DE_N_WEKEN vereist
 * een interval (2-12); WEKENSET vereist precies één van pariteit of een vaste
 * weekset. (TWEEWEKELIJKS is legacy en gedraagt zich als OM_DE_N_WEKEN N=2; de
 * nieuwe UI biedt die vorm niet meer aan maar bestaande waarden blijven geldig.)
 */
export function vereisFrequentieParams(v: FrequentieVelden, ctx: z.RefinementCtx): void {
  const nietWekelijks = v.cadans !== 'WEKELIJKS'
  if (nietWekelijks && v.cadans !== 'TWEEWEKELIJKS') {
    if (!v.eerstvolgendeKeer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eerstvolgendeKeer'],
        message: 'Kies de eerstvolgende keer',
      })
    } else if (typeof v.weekdag === 'number') {
      const dagVanDatum = isoWeekdagVanDatumString(v.eerstvolgendeKeer)
      if (dagVanDatum !== null && dagVanDatum !== v.weekdag) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eerstvolgendeKeer'],
          message: `De datum valt niet op ${WEEKDAG_NAMEN[v.weekdag - 1] ?? 'de gekozen weekdag'}`,
        })
      }
    }
  }
  if (v.cadans === 'OM_DE_N_WEKEN' && (v.intervalWeken == null || v.intervalWeken < 2)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['intervalWeken'],
      message: 'Kies het aantal weken (2-12)',
    })
  }
  if (v.cadans === 'WEKENSET') {
    const heeftPariteit = v.weekPariteit === 'EVEN' || v.weekPariteit === 'ONEVEN'
    const heeftSet = Array.isArray(v.weekSet) && v.weekSet.length > 0
    if (heeftPariteit === heeftSet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['weekPariteit'],
        message: 'Kies even/oneven óf een vaste set weken',
      })
    }
  }
}

// ─── Onderhoudsschema-regel ───────────────────────────────────────────────────

export const onderhoudRegelInputSchema = z
  .object({
    weekdag: z.number().int().min(1, 'Weekdag moet 1-7 zijn').max(7, 'Weekdag moet 1-7 zijn'),
    aantalMonteurs: z.number().int().min(1, 'Minimaal 1 monteur').max(50, 'Maximaal 50 monteurs'),
    cadans: z.enum(ONDERHOUD_CADANS).default('WEKELIJKS'),
    // "Eerstvolgende keer" (jjjj-mm-dd): bij niet-wekelijkse cadans verplicht; de
    // API leidt hier het interne anker (en bij MAANDELIJKS de nDeWeekdag) uit af.
    eerstvolgendeKeer: datumIso.nullable().optional(),
    intervalWeken: z.number().int().min(2).max(12).nullable().optional(),
    weekPariteit: z.enum(WEEK_PARITEIT).nullable().optional(),
    weekSet: z.array(z.number().int().min(1).max(53)).max(53).optional(),
    actief: z.boolean().default(true),
  })
  .superRefine(vereisFrequentieParams)
export type OnderhoudRegelInput = z.infer<typeof onderhoudRegelInputSchema>

export const updateOnderhoudRegelSchema = z
  .object({
    weekdag: z.number().int().min(1).max(7).optional(),
    aantalMonteurs: z.number().int().min(1).max(50).optional(),
    cadans: z.enum(ONDERHOUD_CADANS).optional(),
    eerstvolgendeKeer: datumIso.nullable().optional(),
    intervalWeken: z.number().int().min(2).max(12).nullable().optional(),
    weekPariteit: z.enum(WEEK_PARITEIT).nullable().optional(),
    weekSet: z.array(z.number().int().min(1).max(53)).max(53).optional(),
    actief: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    // Alleen valideren wanneer cadans meegestuurd wordt (anders is het een
    // deel-update van bv. alleen `actief`).
    if (v.cadans !== undefined) {
      vereisFrequentieParams(v as FrequentieVelden, ctx)
    }
  })
export type UpdateOnderhoudRegelInput = z.infer<typeof updateOnderhoudRegelSchema>

// ─── Ontvangers (generiek, FO-311) ────────────────────────────────────────────

export const AUTOMATISERING_ONTVANGER_TYPE = [
  'IKZELF',
  'MEDEWERKER',
  'ROL',
  'KLANT_CONTACT',
  'EXTERN_ADRES',
  'KLANTPORTAAL',
] as const
export type AutomatiseringOntvangerType = (typeof AUTOMATISERING_ONTVANGER_TYPE)[number]

export const AUTOMATISERING_ONTVANGER_TYPE_LABELS: Record<AutomatiseringOntvangerType, string> = {
  IKZELF: 'Ikzelf',
  MEDEWERKER: 'Medewerker(s)',
  ROL: 'Rol',
  KLANT_CONTACT: 'Klant-contactpersoon',
  EXTERN_ADRES: 'Extern e-mailadres',
  KLANTPORTAAL: 'Klantenportaal',
}

/** Interne ontvanger-typen (in-app/push toegestaan). */
export const INTERNE_ONTVANGER_TYPES: readonly AutomatiseringOntvangerType[] = ['IKZELF', 'MEDEWERKER', 'ROL']
/** Klant-/externe ontvanger-typen (alleen e-mail/portaal, klantwaardig). */
export const KLANT_ONTVANGER_TYPES: readonly AutomatiseringOntvangerType[] = ['KLANT_CONTACT', 'KLANTPORTAAL']

/** Rollen die als ROL-ontvanger kunnen dienen (de User.role-waarden die notify() kent). */
export const ONTVANGER_ROLLEN = ['MONTEUR', 'TEAMLEIDER', 'PLANNER', 'HR', 'SALARIS', 'MANAGER', 'ADMIN'] as const
export type OntvangerRol = (typeof ONTVANGER_ROLLEN)[number]

export const ONTVANGER_ROL_LABELS: Record<OntvangerRol, string> = {
  MONTEUR: 'Monteurs',
  TEAMLEIDER: 'Teamleiders',
  PLANNER: 'Planners',
  HR: 'HR',
  SALARIS: 'Salaris',
  MANAGER: 'Managers',
  ADMIN: 'Beheerders',
}

export const automatiseringOntvangerInputSchema = z
  .object({
    type: z.enum(AUTOMATISERING_ONTVANGER_TYPE),
    // Betekenis: MEDEWERKER=Employee.id, ROL=rol-waarde, KLANT_CONTACT=ClientContact.id,
    // EXTERN_ADRES=e-mailadres, KLANTPORTAAL=Client.id, IKZELF=null (API vult de user).
    refId: z.string().trim().min(1).max(200).nullable().optional(),
    inApp: z.boolean().default(false),
    email: z.boolean().default(false),
    push: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (!v.inApp && !v.email && !v.push) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['inApp'], message: 'Kies minstens één kanaal' })
    }
    if (v.type !== 'IKZELF' && !v.refId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['refId'], message: 'Ontvanger is verplicht' })
    }
    if (v.push && !INTERNE_ONTVANGER_TYPES.includes(v.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['push'],
        message: 'Push kan alleen naar interne gebruikers',
      })
    }
    if ((KLANT_ONTVANGER_TYPES.includes(v.type) || v.type === 'EXTERN_ADRES') && (v.inApp || v.push)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Deze ontvanger kan alleen per e-mail/portaal',
      })
    }
    if (v.type === 'ROL' && v.refId && !ONTVANGER_ROLLEN.includes(v.refId as OntvangerRol)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['refId'], message: 'Onbekende rol' })
    }
    if (v.type === 'EXTERN_ADRES' && v.refId && !z.string().email().safeParse(v.refId).success) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['refId'], message: 'Ongeldig e-mailadres' })
    }
  })
export type AutomatiseringOntvangerInput = z.infer<typeof automatiseringOntvangerInputSchema>

export interface AutomatiseringOntvangerDTO {
  id: string
  type: AutomatiseringOntvangerType
  refId: string | null
  /** Server-geresolvede weergavenaam (medewerker/klant/contact/rol/adres). */
  label: string
  inApp: boolean
  email: boolean
  push: boolean
  /** true wanneer de ref niet meer bestaat (medewerker uit dienst / contact verwijderd). */
  vervallen: boolean
}

// ─── Meldingen-dispatcher: resultaat per ontvanger x kanaal (run-historie) ─────

export const ONTVANGER_KANAAL = ['IN_APP', 'EMAIL', 'PUSH', 'KLANTPORTAAL'] as const
export type OntvangerKanaal = (typeof ONTVANGER_KANAAL)[number]

export const ONTVANGER_KANAAL_LABELS: Record<OntvangerKanaal, string> = {
  IN_APP: 'In-app',
  EMAIL: 'E-mail',
  PUSH: 'Push',
  KLANTPORTAAL: 'Klantenportaal',
}

export const ONTVANGER_RESULTAAT_STATUS = [
  'VERSTUURD',
  'OVERGESLAGEN', // bv. klant-opt-in vereist, of geen verwachting deze week
  'GEEN_ONTVANGER', // ref vervallen / geen adres / geen user
  'GEEN_SUBSCRIPTION', // push zonder geregistreerde subscription
  'GEWEIGERD', // door het klant-plafond (opt-out) tegengehouden
  'MISLUKT',
] as const
export type OntvangerResultaatStatus = (typeof ONTVANGER_RESULTAAT_STATUS)[number]

export interface OntvangerResultaatDTO {
  ontvangerId: string
  type: AutomatiseringOntvangerType
  label: string
  kanaal: OntvangerKanaal
  status: OntvangerResultaatStatus
  detail?: string
}

export interface OnderhoudControleRunDTO {
  id: string
  periodeKey: string
  klantnaam: string | null
  status: OnderhoudControleStatusType | string
  geaccepteerd: boolean
  ontvangerResultaten: OntvangerResultaatDTO[]
  createdAt: string
}

// ─── Controle-config (aan het schema gekoppeld) ───────────────────────────────

export const createOnderhoudControleSchema = z.object({
  naam: z.string().min(1, 'Naam is verplicht').max(120).default('Wekelijkse onderhoudscontrole'),
  weekdag: z.number().int().min(1).max(7).default(4),
  doelweekOffset: z.number().int().min(0, 'Offset 0-8').max(8, 'Offset 0-8').default(1),
  klantAutomatischVersturen: z.boolean().default(false),
  actief: z.boolean().default(true),
})
export type CreateOnderhoudControleInput = z.infer<typeof createOnderhoudControleSchema>

export const updateOnderhoudControleSchema = z.object({
  naam: z.string().min(1).max(120).optional(),
  weekdag: z.number().int().min(1).max(7).optional(),
  doelweekOffset: z.number().int().min(0).max(8).optional(),
  klantAutomatischVersturen: z.boolean().optional(),
  actief: z.boolean().optional(),
})
export type UpdateOnderhoudControleInput = z.infer<typeof updateOnderhoudControleSchema>

// ─── Onderhoudsschema (per klant) — one-screen create (schema + controle + ontvangers) ─

export const createKlantOnderhoudSchemaSchema = z.object({
  clientId: z.string().min(1, 'Klant is verplicht'),
  naam: z.string().max(120).nullable().optional(),
  regels: z.array(onderhoudRegelInputSchema).default([]),
  // FO-311 — de controle hoort automatisch bij het schema (default aan). Weggelaten
  // -> de API maakt een default-controle (donderdag / week +1).
  controle: createOnderhoudControleSchema.partial().optional(),
  // Weggelaten -> de API zet de standaard-ontvanger (ikzelf, in-app).
  ontvangers: z.array(automatiseringOntvangerInputSchema).default([]),
})
export type CreateKlantOnderhoudSchemaInput = z.infer<typeof createKlantOnderhoudSchemaSchema>

export const updateKlantOnderhoudSchemaSchema = z.object({
  naam: z.string().max(120).nullable().optional(),
  actief: z.boolean().optional(),
})
export type UpdateKlantOnderhoudSchemaInput = z.infer<typeof updateKlantOnderhoudSchemaSchema>

/** Vervang de volledige ontvangerslijst van een schema-controle of mail in één keer. */
export const setOntvangersSchema = z.object({
  ontvangers: z.array(automatiseringOntvangerInputSchema),
})
export type SetOntvangersInput = z.infer<typeof setOntvangersSchema>

// ─── Voorvallen-preview (server-berekend; geen client-frequentielogica) ────────

export const voorvalPreviewRegelSchema = z.object({
  weekdag: z.number().int().min(1).max(7),
  cadans: z.enum(ONDERHOUD_CADANS),
  eerstvolgendeKeer: datumIso.nullable().optional(),
  intervalWeken: z.number().int().min(2).max(12).nullable().optional(),
  weekPariteit: z.enum(WEEK_PARITEIT).nullable().optional(),
  weekSet: z.array(z.number().int().min(1).max(53)).max(53).optional(),
  aantal: z.number().int().min(1).max(24).optional(),
})
export type VoorvalPreviewRegelInput = z.infer<typeof voorvalPreviewRegelSchema>

export interface OnderhoudVoorvalDTO {
  weekStart: string // yyyy-mm-dd (maandag)
  week: number
  jaar: number
  label: string // "wk 29"
}

// ─── Week-detail voor de WeekStatusBadge-popover ───────────────────────────────

export interface OnderhoudWeekDagDTO {
  weekdag: number
  weekdagLabel: string
  verwacht: number
  gepland: number
  monteurs: string[]
  werkbonnen: string[]
}

export interface OnderhoudWeekDetailDTO {
  weekStart: string // yyyy-mm-dd (maandag)
  weekLabel: string // "wk 29"
  clientId: string
  status: OnderhoudControleStatusType
  totaalVerwacht: number
  totaalGepland: number
  geaccepteerd: boolean
  dagen: OnderhoudWeekDagDTO[]
}

// ─── DTO's ────────────────────────────────────────────────────────────────────

export interface OnderhoudRegelDTO {
  id: string
  weekdag: number
  aantalMonteurs: number
  cadans: OnderhoudCadans
  /** Interne, afgeleide waarden (weergave). */
  ankerDatum: string | null
  ankerWeek: number | null
  intervalWeken: number | null
  nDeWeekdag: number | null
  weekPariteit: WeekPariteit | null
  weekSet: number[]
  /** Server-berekende eerstvolgende keer (voor het terugvullen van de DatePicker). */
  eerstvolgendeKeer: string | null
  actief: boolean
}

/** Live dekking-status van een doelweek (groen/oranje/rood in de UI). */
export interface OnderhoudWeekStatusDTO {
  weekStart: string // yyyy-mm-dd (maandag)
  weekLabel: string // "W30 2026"
  status: OnderhoudControleStatusType
  totaalVerwacht: number
  totaalGepland: number
  geaccepteerd: boolean
}

export interface OnderhoudControleDTO {
  id: string
  schemaId: string | null
  naam: string
  weekdag: number
  doelweekOffset: number
  klantAutomatischVersturen: boolean
  actief: boolean
}

export interface KlantOnderhoudSchemaDTO {
  id: string
  clientId: string
  clientNaam: string
  naam: string | null
  actief: boolean
  regels: OnderhoudRegelDTO[]
  komendeWeken: OnderhoudWeekStatusDTO[]
  /** FO-311 — de gekoppelde controle + ontvangers (one-screen). */
  controle: OnderhoudControleDTO | null
  ontvangers: AutomatiseringOntvangerDTO[]
  /** Kaartmelding (bv. "geen actieve regels" of "ontvanger vervallen"), of null. */
  waarschuwing: string | null
}
